import { getType, isActionOf } from 'typesafe-actions'
import { from, of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators'
import { toast } from 'react-toastify'
import { AnyAction } from 'redux'
import { RootEpic } from '../types'
import {
  getClientTypesForAssignToUserRequest,
  getClientTypesForAssignToUserSuccess,
  getClientTypesForAssignToUserFailure,
  getClientTypesRequest,
  getClientTypesSuccess,
  getClientTypesFailure,
  getClientTypeDetailsRequest,
  getClientTypeDetailsSuccess,
  getClientTypeDetailsFailure,
  createClientTypeRequest,
  createClientTypeSuccess,
  createClientTypeFailure,
  updateClientTypeRequest,
  updateClientTypeSuccess,
  updateClientTypeFailure,
  getListOfClientTypesRequest,
  getListOfClientTypesSuccess,
  getListOfClientTypesFailure,
} from '../actions'

export const getListOfClientTypesEpic: RootEpic = (action$, state$, { fetchListOfClientTypes }) =>
  action$.pipe(
    filter(isActionOf([getListOfClientTypesRequest])),
    switchMap(() =>
      from(
        fetchListOfClientTypes({
          token: state$.value.app.authToken as string,
        }),
      ).pipe(
        map((response) => getListOfClientTypesSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch list of client types.')
          return of(getListOfClientTypesFailure(message))
        }),
      ),
    ),
  )

export const getClientTypesForAssignToUserEpic: RootEpic = (action$, state$, { fetchClientTypesForAssignToUser }) =>
  action$.pipe(
    filter(isActionOf([getClientTypesForAssignToUserRequest])),
    switchMap(({ payload }) =>
      from(
        fetchClientTypesForAssignToUser({
          token: state$.value.app.authToken as string,
          limit: 10,
          ...payload,
        }),
      ).pipe(
        map((response) => getClientTypesForAssignToUserSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch client types.')
          return of(getClientTypesForAssignToUserFailure(message))
        }),
      ),
    ),
  )

export const fetchClientTypesEpic: RootEpic = (action$, state$, { fetchClientTypes }) =>
  action$.pipe(
    filter(isActionOf([getClientTypesRequest])),
    switchMap(({ type, payload }) =>
      from(
        fetchClientTypes({
          token: state$.value.app.authToken as string,
          limit: 10,
          ...state$.value.assets.filters,
          ...(type === getType(getClientTypesRequest) && payload),
        }),
      ).pipe(
        map((response) => getClientTypesSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch client types.')
          return of(getClientTypesFailure(message))
        }),
      ),
    ),
  )

export const getClientTypeDetailsEpic: RootEpic = (action$, state$, { fetchClientTypeDetails }) =>
  action$.pipe(
    filter(isActionOf([getClientTypeDetailsRequest])),
    switchMap(({ payload }) =>
      from(
        fetchClientTypeDetails({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        map((response) => getClientTypeDetailsSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch client type details.')
          return of(getClientTypeDetailsFailure(message))
        }),
      ),
    ),
  )

export const createClientTypeEpic: RootEpic = (action$, state$, { createClientType }) =>
  action$.pipe(
    filter(isActionOf([createClientTypeRequest])),
    switchMap(({ payload }) =>
      from(
        createClientType({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          const actions: Array<AnyAction> = []
          if (response.success) {
            toast.success(response.message)
            actions.push(getClientTypesRequest({ page: 1 }))
          } else {
            toast.error(response.message)
          }
          actions.push(createClientTypeSuccess(response))
          return of(...actions)
        }),
        catchError((error) => {
          let message = error?.response?.data?.message || error?.message || 'Failed to create client type.'

          if (Array.isArray(message)) message = message.join(', ')

          toast.error(message)
          return of(createClientTypeFailure(message))
        }),
      ),
    ),
  )
export const updateClientTypeEpic: RootEpic = (action$, state$, { updateClientType }) =>
  action$.pipe(
    filter(isActionOf([updateClientTypeRequest])),
    switchMap(({ payload }) =>
      from(
        updateClientType({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          const actions: Array<AnyAction> = []
          if (response.success) {
            toast.success(response.message)
            actions.push(getClientTypesRequest({ page: 1 }))
          } else {
            toast.error(response.message)
          }
          actions.push(updateClientTypeSuccess(response))
          return of(...actions)
        }),
        catchError((error) => {
          let message = error?.response?.data?.message || error?.message || 'Failed to update client type.'

          if (Array.isArray(message)) message = message.join(', ')

          toast.error(message)
          return of(updateClientTypeFailure(message))
        }),
      ),
    ),
  )

export default [
  getListOfClientTypesEpic,
  getClientTypesForAssignToUserEpic,
  fetchClientTypesEpic,
  getClientTypeDetailsEpic,
  createClientTypeEpic,
  updateClientTypeEpic,
]
